import { roughLocation } from './rough-location.ts';
import type { Near } from './search-region.ts';

/**
 * "Use my location" for address search — asked ONLY when the person taps it
 * (the browser prompts then, never before), kept in this tab's memory for the
 * session and nowhere else: never stored, never sent to our API. Its only use
 * is as MapKit's search region, rounded to ~0.1° by `regionNear`.
 *
 * A refusal, an error or a timeout is silent: the time-zone hint stays.
 */

let session: Near | null = null;

/** Where to bias toward right now: what they shared, else their time zone's city. */
export function nearMe(): Near | null {
  return session ?? roughLocation();
}

/** Whether this browser can offer "Use my location" at all. */
export function canAskLocation(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.geolocation);
}

/** Ask the browser once (it shows its own prompt). Resolves null on any refusal. */
export function askMyLocation(): Promise<Near | null> {
  if (!canAskLocation()) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          session = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            precision: 'exact',
          };
          resolve(session);
        },
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 },
      );
    } catch {
      resolve(null);
    }
  });
}

/** Test seam: forget what this session was told. */
export function forgetMyLocation(): void {
  session = null;
}
